/**
 * Vesta CFO — brand & product copy aligned with business plan (2026).
 * Use these constants on marketing pages; keep Stripe/legacy tiers in app checkout only.
 */

export const VESTA_PRODUCT_NAME = 'Vesta CFO';
export const VESTA_TAGLINE = 'AI financial intelligence for hotels';

/** Single support, sales, and general contact inbox */
export const VESTA_CONTACT_EMAIL = 'vestacfo@gmail.com';

/** Primary sales calendar — Growth & Enterprise */
export const VESTA_SALES_CALENDAR = 'https://calendly.com/chandaksvar/30min';

/** Alias for demo CTAs (ProductDemoSection, MobileStickyCta) */
export const VESTA_BOOK_DEMO_URL = VESTA_SALES_CALENDAR;

/** Optional Loom embed — set VITE_DEMO_LOOM_URL in Vercel env (e.g. https://www.loom.com/share/…) */
export const VESTA_DEMO_LOOM_URL =
  (import.meta.env.VITE_DEMO_LOOM_URL as string | undefined)?.trim() ?? '';

export const VESTA_RESOURCE_TOPICS = [
  { slug: 'revpar', title: 'RevPAR & ADR' },
  { slug: 'goppar', title: 'GOPPAR & margins' },
  { slug: 'anomalies', title: 'Anomaly alerts' },
  { slug: 'pms', title: 'PMS integrations' },
  { slug: 'briefings', title: 'AI briefings' },
  { slug: 'channels', title: 'OTA & channel mix' },
] as const;

/** Public entry price (Starter only — shown on /pricing, not homepage hero) */
export const VESTA_STARTING_PRICE = '$149';

/** Shipped capabilities — use on Features page and sales collateral */
export const VESTA_AVAILABLE_FEATURES = [
  'Live KPI dashboard (RevPAR, ADR, occupancy, GOPPAR, channel mix)',
  'Daily AI briefings, anomaly alerts & opt-in GM morning email',
  'Ask AI over your property data',
  'Mews PMS sync, CSV imports (metrics, channel, comp, events, room category)',
  'QuickBooks sync (manual + daily job), cash runway & vendor spend trends (MoM)',
  'Budget vs actual, budget occupancy target & channel profitability',
  'Rule-based prescriptive suggestions in briefing (when data supports it)',
  'Reports hub: weekly, 1-month & 6-month owner reports (in-app + email; Print for PDF)',
  'Manual comp set ranking & local events calendar (CSV upload)',
  'Basic forecasting, partner marketplace & in-app notifications',
] as const;

/** Roadmap items — never label as "Available now" without owner sign-off */
export const VESTA_ROADMAP_FEATURES = [
  'Automated STR / OTA comp data feeds',
  'Third-party local events API (PredictHQ-style)',
  'Cloudbeds & Opera PMS connectors',
  'Full RMS-style prescriptive pricing engine',
  'Portfolio rollups at scale for large groups',
] as const;

/** Pricing page footnote — honest limits on comp/events automation */
export const VESTA_PRICING_FOOTNOTE =
  'Comp set and local events work today via CSV upload — automated STR and events API feeds are on our roadmap. Cash runway requires QuickBooks bank account sync.';

/** Below-fold landing plans blurb */
export const VESTA_LANDING_PRICING_SNAPSHOT =
  'Starter from $149/mo includes dashboard, AI briefing, alerts, and owner reports. Comp and events use manual CSV today; automated market feeds are on the roadmap.';

export type VestaSaasTier = {
  id: string;
  name: string;
  audience: string;
  description: string;
  features: readonly string[];
  highlight: boolean;
  cta: string;
  ctaHref: string;
  external?: boolean;
  /** Show dollar amount on pricing page */
  showPrice: boolean;
  price?: string;
  pricePrefix?: string;
  priceDetail?: string;
  /** Headline when price is not shown publicly */
  priceHeadline?: string;
};

/** Marketing pricing — Starter shows starting price; Growth+ are call-led */
export const VESTA_SAAS_TIERS: readonly VestaSaasTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    showPrice: true,
    pricePrefix: 'Starting at',
    price: VESTA_STARTING_PRICE,
    priceDetail: '/month · 1 property',
    audience: 'Independent hotels & inns',
    description:
      'Everything you need to see RevPAR, GOPPAR, and daily AI briefings in one place — without hiring a CFO.',
    features: [
      'Core KPI dashboard (RevPAR, ADR, occupancy, GOPPAR, channel mix)',
      'Daily AI briefing (in-app), opt-in GM email & anomaly alerts',
      'Budget vs actual, budget occupancy target & channel profitability',
      'Mews PMS or CSV import (metrics, channel, comp, events)',
      'QuickBooks sync, cash runway (QB bank balances) & Reports hub',
      'Manual comp set & events calendar (CSV) · in-app notifications',
    ],
    cta: 'Get started free',
    ctaHref: '/auth',
    highlight: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    showPrice: false,
    priceHeadline: 'Tailored for your portfolio',
    priceDetail: '2–15 properties · custom packaging',
    audience: 'Boutique groups & multi-property operators',
    description:
      'Multi-property rollups, Mews sync, team seats, and forecasting — scoped to how you operate. Weekly owner reports and scheduled monthly digests.',
    features: [
      'Everything in Starter',
      'Multi-property dashboard (contact us to scope)',
      'Mews sync, team seats & multiple data sources',
      'Weekly owner report email & scheduled monthly reports',
      'Basic forecasting & partner marketplace',
      'Automated STR comp feeds & events API (roadmap)',
    ],
    cta: 'Schedule a call',
    ctaHref: VESTA_SALES_CALENDAR,
    external: true,
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    showPrice: false,
    priceHeadline: 'Custom partnership',
    priceDetail: '15+ properties · SLAs & procurement',
    audience: 'Management companies & large portfolios',
    description:
      'White-glove onboarding, security review, custom integrations, and dedicated support for complex stacks.',
    features: [
      'Dedicated onboarding & customer success',
      'Custom integrations & API access',
      'SLA, security documentation, and MSAs',
      'Multi-property portfolio analytics',
      '6-month owner reports (scheduled Jan/Jul + on-demand)',
      'Priority roadmap: Cloudbeds, Opera, STR feeds',
    ],
    cta: 'Schedule a call',
    ctaHref: VESTA_SALES_CALENDAR,
    external: true,
    highlight: false,
  },
] as const;

export const VESTA_PRICING_TRUST_POINTS = [
  '14-day free trial — no card required to start',
  'Pilot-friendly onboarding',
  'PMS + CSV — you control the source',
  'Operator-first — not generic accounting',
] as const;

export const VESTA_BRAND_COLORS = {
  navy: '#1B3A5C',
  navyMuted: '#2E6DA4',
  gold: '#C8963E',
  cream: '#F7F4EE',
  mist: '#D6E8F2',
} as const;

export const VESTA_VOICE = {
  headline: 'Direct, calm, operator-first — no generic SMB accounting language.',
  avoid: ['generic small-business bookkeeping', 'leading with price on the homepage'],
  prefer: ['RevPAR', 'GOPPAR', 'PMS', 'anomaly', 'partner marketplace', 'independent hotels'],
} as const;
